import { Paragraph } from '@netexknowledge/smart-components';
import PropTypes from 'prop-types';
import * as React from 'react';

import GradeIcon from '../../../assignments/AssignmentsCard/GradeIcon';
import CustomDonutChart from '../../../common/CustomDonutChart';
import CustomIcon from '../../../common/CustomIcon';
import { ZERO, PASS_THRESHOLD } from '../../../../const';
import {
  getObjectTask,
  getStudentTaskAttempt,
  isGrayState,
  isGreenState,
  isPurpleState,
  isRedState
} from '../../../../utils/gradeUtils';
import * as taskService from '../../../../services/domain/taskService';
import Media from 'react-media';
import { MOBILE_MEDIA_QUERY } from '../../../../utils/mobileUtils';
import GradebookStudentSheet from '../../mobile/GradebookStudentSheet';
import { getStatusLabel } from '../../mobile/gradebookMobileUtils';

class GradesTasksStudentRow extends React.Component {
  static propTypes = {
    studentAttempts: PropTypes.array.isRequired,
    student: PropTypes.object.isRequired,
    resultSummary: PropTypes.array.isRequired,
    tasks: PropTypes.array.isRequired
  };
  state = { sheetOpen: false };

  getColorScore = (grade, initialized, uncompleted, pending) => {
    if (parseInt(grade) === ZERO) return 'gray-bg';
    if (initialized > ZERO || uncompleted > ZERO || pending > ZERO) return 'orange-bg';
    return grade >= PASS_THRESHOLD ? 'green-bg' : 'red-bg';
  };
  loadState(student) {
    const { tasks } = this.props;
    const output = {
      success: ZERO,
      failed: ZERO,
      pending: ZERO,
      initialized: ZERO,
      uncompleted: ZERO,
      score: student.score,
      total: ZERO,
      gradeIcons: [],
      cells: []
    };

    let grades = [];
    tasks.forEach((task, index) => {
      if (!task) {
        return;
      }

      const key = task.id || task._id || index;
      const isStudentAssigned =
        Array.isArray(task.users) && task.users.find(user => user._id === student._id) !== undefined;

      if (!isStudentAssigned) {
        output.gradeIcons.push(<CustomIcon classes={'grade uncomplete'} name="no-eye-view" size={24} key={key} />);
        output.cells.push({ task, key, label: getStatusLabel('no-eye-view') });
        return;
      }

      const studentAttempt = getStudentTaskAttempt(task, student._id);
      const objectProjectTask = studentAttempt ? getObjectTask(task, student._id) : null;
      const rawGrade = taskService.getTaskGrade(task, student._id);
      const parsedGrade =
        typeof rawGrade === 'number'
          ? rawGrade
          : rawGrade !== null && rawGrade !== undefined
          ? parseFloat(rawGrade)
          : null;
      const normalizedGrade = Number.isNaN(parsedGrade) ? null : parsedGrade;
      const studentGrayState = isGrayState(task, objectProjectTask, student._id);
      const isPendingState = isPurpleState(task, objectProjectTask, normalizedGrade, student._id);
      const isGreen = normalizedGrade !== null && isGreenState(task, normalizedGrade, objectProjectTask);
      const isRed = normalizedGrade !== null && isRedState(task, normalizedGrade, objectProjectTask);

      if (studentGrayState) {
        output.uncompleted += 1;
        grades.push(0);
        output.gradeIcons.push(<CustomIcon classes={'grade uncomplete'} name="dots-icon" size={24} key={key} />);
        output.cells.push({ task, key, label: getStatusLabel('dots-icon') });
      } else if (isPendingState) {
        output.pending += 1;
        grades.push(0);
        output.gradeIcons.push(<CustomIcon classes={'grade pending'} name="help-button" size={24} key={key} />);
        output.cells.push({ task, key, label: getStatusLabel('help-button') });
      } else if (isGreen) {
        grades.push(normalizedGrade);
        output.success += 1;
        output.gradeIcons.push(<GradeIcon active={true} grade={normalizedGrade} key={key} notShowText={true} />);
        output.cells.push({ task, key, label: String(normalizedGrade).replace('.', ',') });
      } else if (isRed) {
        grades.push(normalizedGrade);
        output.failed += 1;
        output.gradeIcons.push(<GradeIcon active={true} grade={normalizedGrade} key={key} notShowText={true} />);
        output.cells.push({ task, key, label: String(normalizedGrade).replace('.', ',') });
      } else {
        output.uncompleted += 1;
        grades.push(0);
        output.gradeIcons.push(<CustomIcon classes={'grade uncomplete'} name="dots-icon" size={24} key={key} />);
        output.cells.push({ task, key, label: getStatusLabel('dots-icon') });
      }

      output.total += 1;
    });

    const gradesMedium = grades.reduce((p, n) => p + n, 0) / grades.length;

    output.success = output.total ? (output.success * 100) / output.total : 0;
    output.failed = output.total ? (output.failed * 100) / output.total : 0;
    output.pending = output.total ? (output.pending * 100) / output.total : 0;
    output.initialized = output.total ? (output.initialized * 100) / output.total : 0;
    output.uncompleted = output.total ? (output.uncompleted * 100) / output.total : 0;
    output.score = grades.length > 0 ? gradesMedium : 0;

    return output;
  }
  openSheet = () => this.setState({ sheetOpen: true });

  handleRowKeyDown = event => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.openSheet();
    }
  };

  renderMobile() {
    const { student } = this.props;
    const { success, failed, pending, initialized, uncompleted, score, gradeIcons, cells } = this.loadState(student);
    const grade = score.toFixed(2);
    const gradeText = grade.toString().replace('.', ',');
    const chipClass = this.getColorScore(grade, initialized, uncompleted, pending);
    const studentNameSurname = `${student.name} ${student.surname}`;
    // gradeIcons and cells are pushed together: same index, same task
    const rows = cells.map(({ task, key, label }, index) => ({
      key,
      typeIcon: 'blank-paper-sheet',
      name: task.name,
      label,
      statusNode: gradeIcons[index]
    }));

    return (
      <>
        <div
          role="button"
          tabIndex={0}
          className={student.enabled ? 'student-row' : 'student-row opacity-20'}
          aria-haspopup="dialog"
          onClick={this.openSheet}
          onKeyDown={this.handleRowKeyDown}
        >
          <div className="student-name">
            <img src={student.picture} alt="" className="student-avatar" />
            <div className="student-chart">
              <CustomDonutChart
                success={success}
                failed={failed}
                pending={pending}
                initialized={initialized}
                uncompleted={uncompleted}
                hideText={true}
                donutWidth={3}
                diameter={57}
              />
            </div>
            <span className="student-name-text">{studentNameSurname}</span>
            <div className={`gradebook-custom-chip ${chipClass}`}>{gradeText}</div>
            <i className="custom-icons-right-chevron student-row-chevron" aria-hidden="true" />
          </div>
        </div>
        <GradebookStudentSheet
          open={this.state.sheetOpen}
          onClose={() => this.setState({ sheetOpen: false })}
          name={studentNameSurname}
          avatarUrl={student.picture}
          grade={gradeText}
          chipClass={chipClass}
          rows={rows}
        />
      </>
    );
  }

  render() {
    return (
      <Media query={MOBILE_MEDIA_QUERY}>{isMobile => (isMobile ? this.renderMobile() : this.renderDesktop())}</Media>
    );
  }

  renderDesktop() {
    const { student } = this.props;
    const { success, failed, pending, initialized, uncompleted, score, total, gradeIcons } = this.loadState(student);
    const grade = score.toFixed(2);

    return (
      <div className={student.enabled ? 'student-row' : 'student-row opacity-20'}>
        <div className="student-name">
          <img src={student.picture} alt="presentation" className="student-avatar" />

          <div className="student-chart">
            <CustomDonutChart
              success={success}
              failed={failed}
              pending={pending}
              initialized={initialized}
              uncompleted={uncompleted}
              hideText={true}
              donutWidth={3}
              diameter={57}
              showTooltips={true}
            />
          </div>

          <Paragraph className="text-grey-700 text--1line tooltip top" dataText={`${student.name} ${student.surname}`}>
            <span>{student.name}</span>
          </Paragraph>

          <div className={`gradebook-custom-chip ${this.getColorScore(grade, initialized, uncompleted, pending)}`}>
            {grade.toString().replace('.', ',')}
          </div>
        </div>

        {Boolean(gradeIcons && gradeIcons.length) && gradeIcons.map(gradeIcon => gradeIcon)}
      </div>
    );
  }
}

export default GradesTasksStudentRow;
